import { useState, useEffect } from "react";
import logo from "../assets/argencis-logo.png";
import SelectPill from "./SelectPill";


const PAGE_SIZE = 10;
const MAX_CLIENT_RESULTS = 1000;

const STATUS_OPTIONS = [
  "ALL",
  "In-Process",
  "Pending",
  "Shipped",
  "Out for Delivery",
  "Delivered",
  "Cancelled",
];

const STATUS_COLORS = {
  "In-Process": "#99dff7ff",
  "Pending": "#f3c574ff",
  "Shipped": "#e0dd3eff",
  "Out for Delivery": "#cbb8ebff",
  "Delivered": "#86efac",
  "Cancelled": "#fca5a5ff",
};

export default function PublicSearch() {
  const [clinic, setClinic] = useState("");
  const [patient, setPatient] = useState("");
  const [rawResults, setRawResults] = useState([]);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [physicianFilter, setPhysicianFilter] = useState("ALL"); // F-04
  const [currentPage, setCurrentPage] = useState(1);

  const [openTracking, setOpenTracking] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const toggleTracking = (key) => {
    setOpenTracking((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const display = (value) => {
    if (!value || value.trim?.() === "") return "N/A";
    return value;
  };

  // F-11: strip timestamp from date fields
  const displayDate = (value) => {
    if (!value || value.trim?.() === "") return "N/A";
    return value.split(" ")[0];
  };

  /* -----------------------------------------------------------
      SEARCH
  ----------------------------------------------------------- */
  async function search() {
    if (clinic.trim().length < 3) {
      setError("Clinic name must be at least 3 characters.");
      return;
    }

    setError("");
    setLoading(true);
    setHasSearched(false);
    setCurrentPage(1);
    setOpenTracking({});
    // F-04: reset filters on every new search so stale selections don't hide results
    setStatusFilter("ALL");
    setPhysicianFilter("ALL");

    try {
      const res = await fetch(
        `${API_BASE}/public?clinic=${encodeURIComponent(clinic.trim())}`
      );

      if (!res.ok) throw new Error();

      const data = await res.json();
      console.log("API RAW RESPONSE:", data);
console.log("API RESULTS:", data.results);
console.log("API RESULTS LENGTH:", data.results?.length);
      setRawResults((data.results || []).slice(0, MAX_CLIENT_RESULTS));
      setHasSearched(true);
    } catch {
      setError("Unable to fetch orders.");
      setRawResults([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }

  /* -----------------------------------------------------------
      FILTER + SORT
  ----------------------------------------------------------- */
  let filtered = rawResults;

  if (patient.trim()) {
    const p = patient.toLowerCase();
    filtered = filtered.filter((o) => o.name?.toLowerCase().includes(p));
  }

  if (statusFilter !== "ALL") {
    filtered = filtered.filter((o) => o.status === statusFilter);
  }

  // F-04: physician filter
  if (physicianFilter !== "ALL") {
    filtered = filtered.filter((o) => o.physician === physicianFilter);
  }

  filtered = [...filtered].sort((a, b) => {
    const da = new Date(a.order_received || 0); // F-02/03: order_received replaces shipDate
    const db = new Date(b.order_received || 0); // F-02/03: order_received replaces shipDate
    return sortOrder === "NEWEST" ? db - da : da - db;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [patient, statusFilter, sortOrder, physicianFilter]); // F-04

  console.log("RAW RESULTS STATE:", rawResults);
console.log("FILTERED RESULTS:", filtered);
console.log("FILTERED LENGTH:", filtered.length);


  /* -----------------------------------------------------------
      RENDER
  ----------------------------------------------------------- */
  return (
    <div className="page">
      <div className="container">

        {/* LOGO */}
        <div className="logo">
          <img src={logo} alt="Argencis" />
        </div>

        {/* SEARCH CARD */}
        <div className="card search-card">
          <h2>Order Status Lookup</h2>

          <div className="search-grid">
            <input
              placeholder="Clinic name"
              value={clinic}
              onChange={(e) => setClinic(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
            <input
              placeholder="Patient name (optional)"
              value={patient}
              onChange={(e) => setPatient(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
            <button onClick={search} disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </button>
          </div>

         <div className="filters">
  <SelectPill
    label="Status"
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    options={STATUS_OPTIONS.map((s) => ({
      value: s,
      label: s
    }))}
  />

  <SelectPill
    label="Sort"
    value={sortOrder}
    onChange={(e) => setSortOrder(e.target.value)}
    options={[
      { value: "NEWEST", label: "Newest first" },
      { value: "OLDEST", label: "Oldest first" }
    ]}
  />

  {/* F-04: Physician dropdown - populated from current results */}
  <SelectPill
    label="Physician"
    value={physicianFilter}
    onChange={(e) => setPhysicianFilter(e.target.value)}
    options={[
      { value: "ALL", label: "All Physicians" },
      ...Array.from(
        new Set(
          rawResults
            .map((o) => o.physician)
            .filter((p) => p && p.trim() !== "")
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ value: p, label: p })),
    ]}
  />
</div>


          {error && <p className="error">{error}</p>}
          {loading && <p className="loading">Loading…</p>}
        </div>


        {/* EMPTY */}
        {hasSearched && !loading && filtered.length === 0 && !error && (
          <p style={{ textAlign: "center", color: "#dc2626", marginBottom: "12px" }}>
            No orders found.
          </p>
        )}

        {/* RESULTS */}
        {paginated.map((o, i) => (
          <div key={i} className="card result">
            <div className="row top">
              <div className="name">
                {o.name}
                {o.dob && <span className="dob"> • DOB: {o.dob}</span>}
              </div>

              <span
                className="status"
                style={{ background: STATUS_COLORS[o.status] || "#6b7280" }}
              >
                {o.status}
              </span>
            </div>

            <div className="row dates">
              <span>
                {/* F-02/03: <strong>Shipped:</strong> {display(o.shipDate)} */}
                <strong>Order Received:</strong> {displayDate(o.order_received)}
              </span>

            </div>

            <div className="row bottom">
              <div className="notes">
                <strong>Notes:</strong> {display(o.comments)}
              </div>

              {o.trackingLinks?.length === 1 && (
                <a
                  href={o.trackingLinks[0]}
                  target="_blank"
                  rel="noreferrer"
                  className="track"
                >
                  Track
                </a>
              )}

              {o.trackingLinks?.length > 1 && (
                <button
                  className="tracking-toggle"
                  onClick={() => toggleTracking(i)}
                >
                  Tracking ({o.trackingLinks.length})
                  <span className={`arrow ${openTracking[i] ? "open" : ""}`}>
                    ▸
                  </span>
                </button>
              )}
            </div>

            {openTracking[i] && o.trackingLinks?.length > 1 && (
              <div className="tracking-list">
                {o.trackingLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="track small"
                  >
                    Track {idx + 1}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
              First
            </button>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* CLEAN CSS */}
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }

        .page {
          background: #f8fafc;
          min-height: 100vh;
          padding: 12px;
        }

        .container {
          max-width: 860px;
          margin: 0 auto;
        }

        .logo {
          display: flex;
          justify-content: center;
          margin: 14px 0 18px;
        }

        .logo img {
          height: 36px;
        }

        .card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.05);
        }

        .search-card {
          padding: 14px;
          margin-bottom: 12px;
        }

        h2 {
          margin: 0 0 12px;
          font-size: 17px;
        }

        .search-grid {
          display: grid;
          gap: 8px;
          margin-bottom: 8px;
        }

        .search-grid input {
          padding: 9px;
          font-size: 14px;
          border-radius: 8px;
          border: 1px solid #c7d2fe;
        }

        .search-grid button {
          padding: 9px;
          background: #38c952ff;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
.filters {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.filter-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  padding: 6px 28px 6px 12px;
  border-radius: 999px;
  flex: 1 1 160px;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

/* F-04: shows the currently selected dropdown value */
.filter-value {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.filter-pill select {
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
   position: absolute;          
  inset: 0;                    
  width: 100%;
  height: 100%;

  opacity: 0; 
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.filter-pill::after {
  content: "▾";
  position: absolute;
  right: 12px;
  font-size: 14px;        
  font-weight: 700;  
  color: #334155;
  pointer-events: none;
}

        .result {
          padding: 8px 10px;
          margin-bottom: 8px;
          line-height: 1.3;
        }

        .row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .name {
          font-weight: 600;
          font-size: 14px;
        }

        .dob {
          font-size: 12px;
          color: #64748b;
        }

        .dates {
          font-size: 12px;
          gap: 6px;
        }

        .notes {
          font-size: 12px;
          color: #334155;
          flex: 1;
        }

        .status {
          padding: 2px 6px;
          font-size: 10px;
          border-radius: 999px;
          white-space: nowrap;
        }

        .track {
          padding: 6px 12px;
          background: #2c62d8ff;
          color: #fff;
          border-radius: 24px;
          font-size: 12px;
          font-weight: 600;
          text-decoration: none;
          width: fit-content;
          box-shadow: 0px 3px 8px rgba(40, 80, 180, 0.25);
        }

        .tracking-toggle {
          background: none;
          border: none;
          padding: 0;
          font-size: 12px;
          font-weight: 600;
          color: #2563eb;
          cursor: pointer;
          display: flex;
          align-items: center;
        }

        .arrow { transition: transform 0.2s ease; }
        .arrow.open { transform: rotate(90deg); }

        .tracking-list {
          display: flex;
          gap: 6px;
          margin-top: 4px;
          flex-wrap: wrap;
        }

        .pagination {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 14px;
        }

        .error { color: #dc2626; }
        .loading { color: #334155; }

        @media (min-width: 640px) {
          .search-grid {
            grid-template-columns: 1fr 1fr auto;
          }
        }
      `}</style>
    </div>
  );
}
